/**
 * Phase 4.2b — Auto-register the WebRTC singleton when the operator
 * signs in. Without this, inbound customer calls wouldn't ring the
 * browser at all (Telnyx can't route SIP INVITEs to an unregistered
 * client).
 *
 * Mounted globally so it works on every authenticated route. Renders
 * nothing — it's purely a side-effect component.
 *
 * Disconnects on sign-out so the next user isn't accidentally rung.
 */
import { useEffect } from 'react';
import { onAuthStateChanged, getAuth } from 'firebase/auth';
import { getWebRTC, WEBRTC_DEV_ENABLED } from '../lib/webrtcClient';

export default function WebRTCAutoRegister() {
  useEffect(() => {
    if (!WEBRTC_DEV_ENABLED) return undefined;
    const unsub = onAuthStateChanged(getAuth(), async (user) => {
      const rtc = getWebRTC();
      if (user) {
        try {
          await rtc.register();
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn('[webrtc] auto-register failed', err);
        }
      } else {
        // Never disconnect mid-call — a transient auth flicker must
        // not tear down an in-progress conversation. The SDK will
        // naturally lose its session if the token actually expires.
        const s = rtc.getState?.();
        if (s?.activeCall && !s.activeCall.endedAt) return;
        try { rtc.disconnect(); } catch (_) { /* swallow */ }
      }
    });
    return () => { try { unsub(); } catch (_) { /* swallow */ } };
  }, []);
  return null;
}
